import type { DaySummary, ResolvedDailyRevealMode } from '@/types/reveal';
import WeeklySelectionCard from './WeeklySelectionCard';
import { canEditDaySelection } from '@/utils/dayPickEdit';

type WeeklySelectionsSectionProps = {
  days: DaySummary[];
  gameId: string | null;
  leagueIpEnabled?: boolean;
  mode?: ResolvedDailyRevealMode | null;
  /**
   * Open that day's reveal so the player can change the pick. Without this the list
   * is read-only, which left an earlier day's still-editable pick with no route to
   * it once its reveal window closed.
   */
  onEditDay?: (dayId: string) => void;
  /** Ticking clock from the entry view, so EDIT disappears as the cutoff passes. */
  nowMs?: number;
};

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
};

function formatGameDate(gameDate: string): string {
  const d = new Date(gameDate + 'T12:00:00');
  return d.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS);
}

export default function WeeklySelectionsSection({
  days,
  leagueIpEnabled = false,
  mode,
  onEditDay,
  nowMs,
}: WeeklySelectionsSectionProps) {
  const isPropBased = mode === 'prop_based';
  if (days.length === 0) {
    return (
      <section className="py-4 text-center">
        <p
          className="text-[#8a8a8a]"
          style={{ fontFamily: 'Inter, sans-serif', fontSize: '13px', fontWeight: 500 }}
        >
          Graded picks will show up here once your games are scored.
        </p>
      </section>
    );
  }

  return (
    <section>
      {days.map(day => {
        if (!day.userEntry?.selectedCard) return null;

        const card = day.userEntry.selectedCard;
        const totalHits = day.userEntry.totalHits ?? 0;
        const isGraded = day.userEntry.status === 'graded';
        const propWon =
          day.userEntry.propResultStatus === 'won' || day.userEntry.propResultStatus === 'credited';
        const won = isPropBased
          ? isGraded && (propWon || totalHits > 0)
          : isGraded && totalHits > 0;
        const ppdCredited = (day.userEntry.ppdHits ?? 0) > 0;
        // 🔒 pickCutoffAt only — never day.status. See canEditDaySelection.
        const canEdit = onEditDay != null && canEditDaySelection(day, nowMs ?? Date.now());

        return (
          <WeeklySelectionCard
            key={day.id}
            dayId={day.id}
            card={card}
            dateLabel={formatGameDate(day.gameDate)}
            totalHits={totalHits}
            isGraded={isGraded}
            won={won}
            ppdCredited={ppdCredited}
            ppdStatus={day.userEntry.ppdStatus ?? null}
            dayStatus={day.status}
            leagueIpEnabled={leagueIpEnabled}
            isPropBased={isPropBased}
            categoryName={day.categoryName}
            propDisplayLabel={day.userEntry.propDisplayLabel}
            propResultStatus={day.userEntry.propResultStatus}
            propResultValue={day.userEntry.propResultValue}
            propThreshold={day.userEntry.propThreshold}
            propOperator={day.userEntry.propOperator}
            onEdit={canEdit && onEditDay ? () => onEditDay(day.id) : undefined}
          />
        );
      })}
    </section>
  );
}
